import { memo, useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import { Trans } from 'next-i18next';
import Footer from './Footer';
import type FooterType from '../../../types/Footer';
import NavBar from './NavBar';
import FooterMini from './FooterMini';
import useIsStickyOnFooter from '../../core/hooks/useIsStickyOnFooter';
import { ContentType, MiniSiteTabOptions } from '../../core/constants';
import analytics from '../../core/analytics/analytics';
import tailwindConfig from '../../../tailwind.config';
import NewTab from './Icons/NewTab';
import useTotalHotelCount from '../../core/hooks/useTotalHotelCount';
import HotelCount from '../../../types/HotelCount';
import type NotificationAlertType from '../../../types/NotificationAlert';
import MobilePrompt from './MobilePrompt';

const Sidebar = dynamic(() => import('./Sidebar'));
const NotificationAlert = dynamic(() => import('../home/NotificationAlert'));

type Header = {
  showSearchIcon?: boolean;
  shadow?: boolean;
  children?: React.ReactNode;
};

type FooterProps = {
  mini?: boolean;
  content?: FooterType | undefined;
};

type Props = {
  children: React.ReactNode;
  header?: Header;
  footer: FooterProps;
  isNavBarSticky?: boolean;
  hotelCount?: HotelCount | number;
  notificationAlert?: NotificationAlertType;
  setChildrenOffsetTop?: (offsetTop: number) => void;
};
const PageLayout: React.FC<Props> = ({
  children,
  footer = {
    mini: false,
  },
  header = {
    showSearchIcon: false,
    shadow: true,
  },
  isNavBarSticky = false,
  hotelCount,
  notificationAlert,
  setChildrenOffsetTop,
}) => {
  const { pathname, query } = useRouter();
  const isTopPage = pathname === '/';
  const tab = query.tab as MiniSiteTabOptions;
  const [showSidebar, setShowSidebar] = useState<boolean>(false);
  const [ref, isSticky] = useIsStickyOnFooter<HTMLDivElement>({ delay: 10 });
  const totalHotelCount = useTotalHotelCount(hotelCount);

  const events = {
    onShowSidebar: (value: boolean) => {
      setShowSidebar(value);
    },
    onClickFeedbackButton: () => {
      window.open('/feedback/website', '_blank');
      analytics.google.basic.track.select(ContentType.FEEDBACK, 'website');
    },
  };

  const showFeedbackBanner =
    isTopPage ||
    (pathname === '/h/[hotel]' && [undefined, MiniSiteTabOptions.HOTEL_DETAILS].includes(tab));
  const childrenRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const updateOffset = () => {
      if (!childrenRef.current || !setChildrenOffsetTop) return;
      // distance from the top of the page:
      setChildrenOffsetTop(childrenRef.current.getBoundingClientRect().top);
    };

    // Initial set
    updateOffset();

    // Observe size changes
    const observer = new window.ResizeObserver(updateOffset);

    if (childrenRef.current) {
      observer.observe(childrenRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [setChildrenOffsetTop]);

  return (
    <>
      <div className="relative z-50 flex min-h-screen w-full flex-col shadow-4xl">
        <header
          className={classNames('top-0 z-50 w-full h-fit', {
            // Apply fixed position to the header when overlapping background.
            fixed: isTopPage,
            sticky: !isTopPage,
          })}
        >
          {notificationAlert && (
            <NotificationAlert message={notificationAlert.message} url={notificationAlert.url} />
          )}

          <MobilePrompt />
          <NavBar
            showSearchBar={header?.showSearchIcon}
            hasShadow={header?.shadow as boolean}
            onShowSidebar={events.onShowSidebar}
            showSidebar={showSidebar}
            isNavBarSticky={isNavBarSticky}
          />
          <div ref={childrenRef}>{header?.children}</div>
        </header>
        {children}
        {footer?.mini ? (
          <FooterMini copyright={footer?.content?.copyright} />
        ) : (
          <div className="relative">
            {process.env.NEXT_PUBLIC_FEATURE_FLAG_FEEDBACK === 'true' && showFeedbackBanner && (
              <div
                className={classNames('overflow-x-clip left-0 w-20 h-20 z-10', {
                  'fixed bottom-0': !isSticky,
                  'absolute -top-20': isSticky,
                })}
              >
                <div className="absolute left-[-55px] top-6 size-28 rotate-45 bg-[#EAFFFE]" />
                <div
                  onClick={events.onClickFeedbackButton}
                  tabIndex={0}
                  role="button"
                  onKeyDown={events.onClickFeedbackButton}
                  className="absolute left-[-38px] top-8 flex h-14 w-30 rotate-45 flex-col items-center justify-evenly text-center text-[10px] font-bold leading-3 text-teal-600"
                >
                  <Trans i18nKey="common:feedback" />
                  <div className="-rotate-45">
                    <NewTab size={16} color={tailwindConfig.theme.colors.teal[600]} />
                  </div>
                </div>
              </div>
            )}
            <div ref={ref} className="relative z-10">
              <Footer
                hotelCount={totalHotelCount}
                copyright={footer?.content?.copyright}
                links={footer?.content?.footerLinks || []}
              />
            </div>
          </div>
        )}
      </div>
      <Sidebar showSidebar={showSidebar} onShowSidebar={events.onShowSidebar} />
    </>
  );
};

export default memo(PageLayout);
